import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FloppyDisk, EnvelopeSimple, CheckCircle, WarningCircle, Plus } from "@phosphor-icons/react";
import { formatDate } from "@/lib/utils";
import TemplateList from "./TemplateList";
import TemplateEditor from "./TemplateEditor";
import EmailLogPanel from "./EmailLogPanel";

export default function EmailRoutingTab({ settings, onSaved }) {
  const [form, setForm] = useState({
    email_sender_address: settings.email_sender_address || settings.shared_mailbox || "",
    email_sender_name: settings.email_sender_name || "Barnard Inc Lead Tracker",
    email_notifications_enabled: !!settings.email_notifications_enabled,
    auto_assign_by_name_enabled: !!settings.auto_assign_by_name_enabled,
    file_team_email: settings.file_team_email || "",
    app_base_url: settings.app_base_url || window.location.origin,
  });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ placeholders: [], triggers: [] });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = async () => {
    const [t, m] = await Promise.all([
      api.get("/email/templates"),
      api.get("/email/placeholders"),
    ]);
    setTemplates(t.data);
    setMeta(m.data);
  };

  useEffect(() => { fetchAll(); }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", form);
      toast.success("Email routing settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTemplateUpdated = async () => {
    await fetchAll();
  };

  const senderReady = settings.microsoft_configured && form.email_sender_address;

  return (
    <div className="space-y-8">
      {/* Sender Config */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-sm bg-[#0078D4]/10 flex items-center justify-center shrink-0">
            <EnvelopeSimple size={26} weight="fill" className="text-[#0078D4]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl text-slate-900">Email Routing</h2>
              {senderReady && form.email_notifications_enabled ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700">
                  <CheckCircle size={12} weight="fill" /> Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-amber-50 text-amber-700">
                  <WarningCircle size={12} weight="fill" />
                  {!settings.microsoft_configured ? "Microsoft 365 not configured" : !form.email_sender_address ? "Sender not set" : "Notifications disabled"}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Outbound emails are sent via Microsoft Graph using the sender below. Automated triggers (e.g. "Lead assigned to attorney") fire from the templates further down.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-tracking">Sender Email Address</Label>
            <Input
              data-testid="sender-address-input"
              value={form.email_sender_address}
              onChange={(e) => setForm({ ...form, email_sender_address: e.target.value })}
              placeholder="Leads@barnardinc.co.za"
              className="mt-2 rounded-sm h-10 font-mono text-xs"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Must have <span className="font-mono">Mail.Send</span> (or <span className="font-mono">Mail.Send.Shared</span> for shared mailboxes) granted to the Azure app.
            </p>
          </div>
          <div>
            <Label className="label-tracking">Sender Display Name</Label>
            <Input
              data-testid="sender-name-input"
              value={form.email_sender_name}
              onChange={(e) => setForm({ ...form, email_sender_name: e.target.value })}
              className="mt-2 rounded-sm h-10 text-sm"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tracking">App Base URL (for {`{{lead.url}}`} links)</Label>
            <Input
              data-testid="app-base-url-input"
              value={form.app_base_url}
              onChange={(e) => setForm({ ...form, app_base_url: e.target.value })}
              placeholder="https://lead-capture-app-9.preview.emergentagent.com"
              className="mt-2 rounded-sm h-10 font-mono text-xs"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tracking">File Team Email (Won-lead handover PDFs)</Label>
            <Input
              data-testid="file-team-email-input"
              type="email"
              value={form.file_team_email}
              onChange={(e) => setForm({ ...form, file_team_email: e.target.value })}
              placeholder="files@barnardinc.co.za"
              className="mt-2 rounded-sm h-10 font-mono text-xs"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              When a lead is marked <span className="font-medium text-slate-600">Won</span>, the branded
              handover PDF is automatically emailed to all assignees and (optionally) this address.
            </p>
          </div>
          <div className="sm:col-span-2 flex items-center justify-between pt-2">
            <div>
              <Label className="label-tracking">Enable automated notifications</Label>
              <p className="text-xs text-slate-500 mt-1">If off, no automated email triggers will fire. Manual sends still work.</p>
            </div>
            <Switch
              data-testid="notifications-enabled-switch"
              checked={form.email_notifications_enabled}
              onCheckedChange={(v) => setForm({ ...form, email_notifications_enabled: v })}
            />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between pt-2 border-t border-slate-100">
            <div>
              <Label className="label-tracking">Auto-assign new leads by name match</Label>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                When a new lead arrives, scan the body, subject, and AI summary for an active user's first name. If <em>exactly one</em> active user matches, auto-assign the lead to them and fire the <span className="font-mono text-slate-700">lead_assigned</span> email alongside the regular <span className="font-mono text-slate-700">lead_new</span> notification. Ambiguous matches (e.g. two users sharing a first name, or multiple names found) are skipped.
              </p>
            </div>
            <Switch
              data-testid="auto-assign-by-name-switch"
              checked={form.auto_assign_by_name_enabled}
              onCheckedChange={(v) => setForm({ ...form, auto_assign_by_name_enabled: v })}
            />
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100 flex justify-end">
          <Button data-testid="save-email-settings-btn" onClick={onSave} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
            <FloppyDisk size={14} /> {saving ? "Saving…" : "Save email settings"}
          </Button>
        </div>
      </section>

      {/* Templates */}
      <section className="bg-white border border-slate-200 rounded-sm">
        <div className="p-6 border-b border-slate-200 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="label-tracking">Library</div>
            <h3 className="font-display text-2xl text-slate-900 mt-1">Email Templates</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Each trigger uses the first <em>enabled</em> template matching it. Use placeholders like
              <code className="ml-1 px-1.5 py-0.5 bg-slate-100 rounded text-[11px] font-mono">{`{{lead.name}}`}</code> in subject and body.
            </p>
          </div>
          <Button
            data-testid="add-template-btn"
            onClick={() => { setSelectedTemplate(null); setShowCreate(true); }}
            className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
          >
            <Plus size={14} /> New template
          </Button>
        </div>
        <TemplateList
          templates={templates}
          triggers={meta.triggers}
          onSelect={(t) => { setSelectedTemplate(t); setShowCreate(false); }}
          onChange={onTemplateUpdated}
          selectedId={selectedTemplate?.id}
        />
      </section>

      {/* Editor dialog */}
      {(selectedTemplate || showCreate) && (
        <TemplateEditor
          template={selectedTemplate}
          triggers={meta.triggers}
          placeholders={meta.placeholders}
          onClose={() => { setSelectedTemplate(null); setShowCreate(false); }}
          onSaved={() => { fetchAll(); }}
        />
      )}

      {/* Log */}
      <EmailLogPanel />
    </div>
  );
}
